import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Plus, GripVertical, Edit3, Check, X } from "lucide-react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";

interface BasicInfoField {
  id: string;
  label: string;
  value: string;
  order: number;
  required: boolean;
}

interface DynamicBasicInfoCardProps {
  settings: any;
  updateSettings: (updates: any) => Promise<void>;
}

export const DynamicBasicInfoCard = ({
  settings,
  updateSettings,
}: DynamicBasicInfoCardProps) => {
  const { toast } = useToast();
  const [basicInfoFields, setBasicInfoFields] = useState<BasicInfoField[]>([]);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [isAddingField, setIsAddingField] = useState(false);

  useEffect(() => {
    if (settings?.basic_info_fields) {
      const fields = Array.isArray(settings.basic_info_fields)
        ? settings.basic_info_fields
        : JSON.parse(settings.basic_info_fields || "[]");

      // تحديث القيم القديمة لتكون أكثر عمومية
      const updatedFields = fields.map((field: BasicInfoField) => {
        if (field.id === "education_department") {
          return {
            ...field,
            label: "إدارة التعليم",
            value:
              field.value.includes("بالرياض") ||
              field.value.includes("بالمنطقة")
                ? "إدارة التعليم"
                : field.value,
          };
        }
        if (field.id === "education_office") {
          return {
            ...field,
            label: "مكتب التعليم",
            value:
              field.value.includes("بالرياض") ||
              field.value.includes("بالمنطقة")
                ? "مكتب التعليم"
                : field.value,
          };
        }
        return field;
      });

      // إضافة حقل اسم المدرسة إذا لم يكن موجوداً
      const hasSchoolName = updatedFields.some(
        (field: BasicInfoField) => field.id === "school_name"
      );

      if (!hasSchoolName && settings.school_name) {
        updatedFields.push({
          id: "school_name",
          label: "اسم المدرسة",
          value: settings.school_name,
          order: 5,
          required: true,
        });
      }

      setBasicInfoFields(
        updatedFields.sort(
          (a: BasicInfoField, b: BasicInfoField) => a.order - b.order
        )
      );
    } else {
      // إنشاء الحقول الافتراضية مع اسم المدرسة بعد مكتب التعليم
      const defaultFields = [
        {
          id: "country",
          label: "الدولة",
          value: "المملكة العربية السعودية",
          order: 1,
          required: true,
        },
        {
          id: "ministry",
          label: "الوزارة",
          value: "وزارة التعليم",
          order: 2,
          required: true,
        },
        {
          id: "education_department",
          label: "إدارة التعليم",
          value: "إدارة التعليم",
          order: 3,
          required: true,
        },
        {
          id: "education_office",
          label: "مكتب التعليم",
          value: "مكتب التعليم",
          order: 4,
          required: false,
        },
        {
          id: "school_name",
          label: "اسم المدرسة",
          value: settings?.school_name || "",
          order: 5,
          required: true,
        },
      ];
      setBasicInfoFields(defaultFields);
    }
  }, [settings?.basic_info_fields, settings?.school_name]);

  const validateInput = (input: string): boolean => {
    return input.trim().length >= 2 && input.trim().length <= 100;
  };

  const sanitizeInput = (input: string): string => {
    // إزالة السكريبت فقط والحفاظ على المسافات
    return input.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      ""
    );
  };

  const handleFieldChange = (fieldId: string, newValue: string) => {
    const sanitizedValue = sanitizeInput(newValue);
    setBasicInfoFields((prev) =>
      prev.map((field) =>
        field.id === fieldId ? { ...field, value: sanitizedValue } : field
      )
    );
  };

  const handleFieldLabelChange = (fieldId: string, newLabel: string) => {
    const sanitizedLabel = sanitizeInput(newLabel);
    setBasicInfoFields((prev) =>
      prev.map((field) =>
        field.id === fieldId ? { ...field, label: sanitizedLabel } : field
      )
    );
  };

  const handleDeleteField = (fieldId: string) => {
    const field = basicInfoFields.find((f) => f.id === fieldId);
    if (field?.required) {
      toast({
        title: "لا يمكن الحذف",
        description: "هذا الحقل مطلوب ولا يمكن حذفه",
        variant: "destructive",
      });
      return;
    }

    setBasicInfoFields((prev) => prev.filter((field) => field.id !== fieldId));
  };

  const handleAddField = () => {
    if (!validateInput(newFieldLabel)) {
      toast({
        title: "خطأ في التحقق",
        description: "يجب أن يكون اسم الحقل بين 2 و 100 حرف",
        variant: "destructive",
      });
      return;
    }

    const newField: BasicInfoField = {
      id: `custom_${Date.now()}`,
      label: sanitizeInput(newFieldLabel),
      value: "",
      order: Math.max(...basicInfoFields.map((f) => f.order), 0) + 1,
      required: false,
    };

    setBasicInfoFields((prev) => [...prev, newField]);
    setNewFieldLabel("");
    setIsAddingField(false);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(basicInfoFields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const reorderedFields = items.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    setBasicInfoFields(reorderedFields);
  };

  const handleSaveAllSettings = async () => {
    // التحقق من صحة جميع البيانات
    const fieldsValid = basicInfoFields.every(
      (field) => !field.required || validateInput(field.value)
    );

    if (!fieldsValid) {
      toast({
        title: "خطأ في التحقق",
        description: "يرجى ملء جميع الحقول المطلوبة",
        variant: "destructive",
      });
      return;
    }

    try {
      // استخراج اسم المدرسة من الحقول
      const schoolNameField = basicInfoFields.find(
        (field) => field.id === "school_name"
      );

      await updateSettings({
        school_name: schoolNameField?.value || "",
        basic_info_fields: basicInfoFields.filter(
          (field) => field.id !== "school_name"
        ),
      });

      toast({
        title: "تم الحفظ بنجاح",
        description: "تم تحديث معلومات المدرسة الأساسية بنجاح",
      });
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "خطأ في الحفظ",
        description: error.message || "لم نتمكن من حفظ الإعدادات",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="w-full" dir="rtl">
      <CardHeader className="bg-secondary text-white rounded-t-lg">
        <CardTitle className="text-right text-lg text-white flex items-center gap-2">
          <span>معلومات المدرسة الاساسية</span>
        </CardTitle>
        <p className="text-sm text-white/90 text-right">
          أدخل معلومات المدرسة الأساسية والجهات التابعة لها
        </p>
      </CardHeader>
      <CardContent className="space-y-4 pt-4 pb-4" dir="rtl">
        {/* الحقول الديناميكية مع اسم المدرسة */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="fields">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-1"
              >
                {basicInfoFields.map((field, index) => (
                  <Draggable
                    key={field.id}
                    draggableId={field.id}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="border rounded-lg p-3 bg-background animate-fade-in"
                      >
                        <div className="flex items-center gap-3" dir="rtl">
                          <div
                            {...provided.dragHandleProps}
                            className="cursor-move"
                          >
                            <GripVertical className="h-4 w-4 text-muted-foreground" />
                          </div>

                          <div className="flex-1 space-y-2">
                            <>
                              <div className="flex items-center justify-between">
                                <Label className="text-sm text-right font-medium">
                                  {field.label}{" "}
                                  {field.required && (
                                    <span className="text-destructive">*</span>
                                  )}
                                </Label>
                              </div>
                              <Input
                                value={field.value}
                                onChange={(e) =>
                                  handleFieldChange(field.id, e.target.value)
                                }
                                placeholder={`أدخل ${field.label}`}
                                className="text-right"
                                dir="rtl"
                              />
                            </>
                          </div>

                          {!field.required && field.id !== "school_name" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteField(field.id)}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive hover-scale"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* إضافة حقل جديد */}
        {isAddingField ? (
          <div
            className="border rounded-lg p-3 bg-background space-y-2 animate-scale-in"
            dir="rtl"
          >
            <Input
              value={newFieldLabel}
              onChange={(e) => setNewFieldLabel(e.target.value)}
              placeholder="اسم الحقل الجديد"
              className="text-right"
              dir="rtl"
              onKeyPress={(e) => e.key === "Enter" && handleAddField()}
            />
            <div className="flex gap-2 justify-start">
              <Button
                size="sm"
                onClick={handleAddField}
                className="hover-scale"
              >
                إضافة
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsAddingField(false);
                  setNewFieldLabel("");
                }}
                className="hover-scale"
              >
                إلغاء
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={() => setIsAddingField(true)}
            className="w-full border-dashed hover-scale"
            dir="rtl"
          >
            <Plus className="h-4 w-4 ml-2" />
            إضافة حقل جديد
          </Button>
        )}

        <Button
          onClick={handleSaveAllSettings}
          className="w-full hover-scale"
          disabled={basicInfoFields.some(
            (field) => field.required && !validateInput(field.value)
          )}
          dir="rtl"
        >
          حفظ المعلومات الأساسية
        </Button>
      </CardContent>
    </Card>
  );
};
